import React, { useRef, useState } from 'react';
import { Download, Upload, FileText, Settings, AlertTriangle } from 'lucide-react';
import { useAppStore, clearAllAppData, importAppData, ImportError, IMPORT_LIMITS, hydrateSessionsForExport } from '@/stores/appStore';
import { useTranslation } from '@/i18n';

/** At most five names, each cut short: the keys come from an arbitrary file. */
function formatSkippedKeys(keys: string[]): string {
  const shown = keys.slice(0, 5).map((k) => (k.length > 40 ? `${k.slice(0, 40)}…` : k));
  return keys.length > shown.length ? `${shown.join(', ')} (+${keys.length - shown.length})` : shown.join(', ');
}

export const ImportExportSettings: React.FC = () => {
  const { sessions, mcpServers, llmProviders, settings } = useAppStore();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);

  const downloadJson = (data: unknown, filename: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportData = async (type: 'all' | 'sessions' | 'settings') => {
    if (exporting) return;

    if (type === 'settings') {
      downloadJson(
        {
          mcpServers,
          llmProviders,
          settings,
          exportedAt: new Date().toISOString(),
          type: 'settings',
        },
        `mcp-chat-settings-${new Date().toISOString().split('T')[0]}.json`
      );
      return;
    }

    setExporting(true);
    try {
      const { sessions: hydratedSessions, skippedUnsaved } = await hydrateSessionsForExport(sessions);
      // Empty chats the server never saved are left out, and the file says so.
      const skippedNote = skippedUnsaved > 0 ? { unsavedEmptyChatsSkipped: skippedUnsaved } : {};
      const date = new Date().toISOString().split('T')[0];

      if (type === 'all') {
        downloadJson(
          {
            sessions: hydratedSessions,
            ...skippedNote,
            mcpServers,
            llmProviders,
            settings,
            exportedAt: new Date().toISOString(),
            version: '1.0.1',
          },
          `mcp-chat-backup-${date}.json`
        );
      } else {
        downloadJson(
          {
            sessions: hydratedSessions,
            ...skippedNote,
            exportedAt: new Date().toISOString(),
            type: 'sessions',
          },
          `mcp-chat-sessions-${date}.json`
        );
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(t.errors.generic);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = () => {
    if (importing) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const asMb = (bytes: number) => Math.max(1, Math.round(bytes / (1024 * 1024)));

    // Bound before a single byte is read, let alone sent.
    if (file.size > IMPORT_LIMITS.maxBytes) {
      alert(
        t.settings.importTooLarge
          .replace('{size}', String(asMb(file.size)))
          .replace('{limit}', String(asMb(IMPORT_LIMITS.maxBytes)))
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      setImporting(true);
      setImportProgress(null);

      // Parsing is the only step where the FILE can be at fault, so it is the
      // only step allowed to say "invalid input".
      let data: Record<string, unknown>;
      try {
        const parsed = JSON.parse(String(e.target?.result ?? ''));
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Invalid file format');
        }
        data = parsed;
      } catch (error) {
        console.error('Import parse error:', error);
        alert(t.errors.invalidInput);
        setImporting(false);
        return;
      }

      try {
        const result = await importAppData(data, (done, total) => setImportProgress({ done, total }));
        // A key the file carried but we did not apply is said, never hidden
        // behind "imported successfully" (STORY-331).
        alert(
          result.settingsRejected.length > 0
            ? `${t.success.imported}. ${t.settings.importSettingsSkipped.replace(
                '{keys}',
                formatSkippedKeys(result.settingsRejected)
              )}`
            : t.success.imported
        );
        window.location.reload();
      } catch (error) {
        console.error('Import error:', error);
        setImportProgress(null);

        // 'format' and 'limit' both mean nothing was sent — no reload needed.
        if (error instanceof ImportError && error.kind === 'format') {
          alert(t.errors.invalidInput);
          setImporting(false);
          return;
        }
        if (error instanceof ImportError && error.kind === 'limit' && error.limit) {
          const template =
            error.limit.of === 'sessions'
              ? t.settings.importTooManySessions
              : t.settings.importTooManyMessages;
          alert(
            template
              .replace('{actual}', String(error.limit.actual))
              .replace('{allowed}', String(error.limit.allowed))
          );
          setImporting(false);
          return;
        }

        // Transport: some of the backup may already be on the server. Say how
        // much landed — and reload, or those chats stay invisible.
        const progress = error instanceof ImportError ? error.progress : undefined;
        const reason = error instanceof Error ? error.message : String(error);
        alert(
          t.settings.importPartialFailure
            .replace('{imported}', String(progress?.sessionsImported ?? 0))
            .replace('{total}', String(progress?.sessionsTotal ?? 0))
            .replace('{error}', reason)
        );
        window.location.reload();
      }
    };

    reader.onerror = () => {
      alert(t.errors.invalidInput);
    };

    reader.readAsText(file);

    // Reset the input so the same file can be chosen again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearAllData = async () => {
    if (!window.confirm(t.settings.clearAllDataConfirm)) return;
    if (clearing) return;

    setClearing(true);
    try {
      const result = await clearAllAppData();
      const warnings: string[] = [];
      if (result.failed > 0) {
        warnings.push(
          t.settings.clearAllDataPartial
            .replace('{deleted}', String(result.deleted))
            .replace('{total}', String(result.total))
            .replace('{failed}', String(result.failed))
        );
      }
      if (result.settingsFailed) {
        warnings.push(t.settings.clearAllDataSettingsNotReset);
      }
      if (warnings.length > 0) {
        alert(warnings.join(' '));
      }
      // Reload on both branches: the local state is already wiped, so the
      // sidebar must be re-read from the server or it lies about what is left.
      window.location.reload();
    } catch (error) {
      console.error('Clear all data error:', error);
      alert(t.errors.generic);
      window.location.reload();
    }
  };

  const exportButtonClass =
    'flex flex-col items-center p-6 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-300 dark:hover:border-blue-500 transition-colors group disabled:opacity-50 disabled:pointer-events-none disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600';

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {t.settings.importExport}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t.settings.importExportDescription}
        </p>
      </div>

      {/* Export Section */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.common.export}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => exportData('all')}
            disabled={exporting}
            className={exportButtonClass}
          >
            <Download className="h-8 w-8 text-gray-400 group-hover:text-blue-500 mb-3" />
            <h5 className="font-medium text-gray-900 dark:text-white mb-1">
              {t.settings.completeBackup}
            </h5>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {exporting ? t.common.loading : t.settings.exportAllDataDescription}
            </p>
          </button>
          
          <button
            type="button"
            onClick={() => exportData('sessions')}
            disabled={exporting}
            className={exportButtonClass}
          >
            <FileText className="h-8 w-8 text-gray-400 group-hover:text-blue-500 mb-3" />
            <h5 className="font-medium text-gray-900 dark:text-white mb-1">
              {t.settings.chatSessions}
            </h5>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {exporting ? t.common.loading : t.settings.exportSessionsDescription}
            </p>
          </button>
          
          <button
            type="button"
            onClick={() => exportData('settings')}
            disabled={exporting}
            className={exportButtonClass}
          >
            <Settings className="h-8 w-8 text-gray-400 group-hover:text-blue-500 mb-3" />
            <h5 className="font-medium text-gray-900 dark:text-white mb-1">
              {t.settings.settingsOnly}
            </h5>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {t.settings.exportSettingsDescription}
            </p>
          </button>
        </div>
      </div>

      {/* Import Section */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.importData}
        </h4>
        
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-6">
          <button
            type="button"
            onClick={handleImport}
            disabled={importing}
            className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            <Upload className="h-4 w-4" />
            <span>
              {!importing
                ? t.settings.chooseFileToImport
                : importProgress
                  ? t.settings.importProgress
                      .replace('{done}', String(importProgress.done))
                      .replace('{total}', String(importProgress.total))
                  : t.common.loading}
            </span>
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
            {t.settings.importDescription}
          </p>
        </div>
      </div>

      {/* Data Summary */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.currentDataSummary}
        </h4>
        
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t.settings.chatSessionsLabel}</span>
            <span className="font-medium text-gray-900 dark:text-white">{sessions.length}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t.settings.mcpServersLabel}</span>
            <span className="font-medium text-gray-900 dark:text-white">{mcpServers.length}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t.settings.llmProvidersLabel}</span>
            <span className="font-medium text-gray-900 dark:text-white">{llmProviders.length}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t.settings.totalMessagesLabel}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {sessions.reduce(
                (total, session) => total + (session.messageCount ?? session.messages.length),
                0
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-red-700 dark:text-red-400">
          {t.settings.dangerZone}
        </h4>
        
        <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
            <div className="flex-1">
              <h5 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">
                {t.settings.clearAllData}
              </h5>
              <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                {t.settings.clearAllDataWarning}
              </p>
              <button
                type="button"
                onClick={clearAllData}
                disabled={clearing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                {clearing ? t.common.loading : t.settings.clearAllData}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
